<template>
  <van-overlay :show="visible" class="eula-overlay" :z-index="100001" :lock-scroll="false">
    <div class="eula-shell" @click.stop>
      <div class="eula-card">
        <header class="eula-header">
          <h1 class="eula-title">用户协议与免责声明</h1>
          <p class="eula-sub">
            使用本软件前，请完整阅读以下条款。您支付的费用仅与激活码所代表的软件授权服务相关。
          </p>
        </header>

        <div class="eula-scroll" role="region" aria-label="协议正文">
          <section class="eula-block">
            <h2>一、服务性质与对价范围</h2>
            <p>
              您所支付的费用<strong>仅用于购买本软件在约定期间内的使用权</strong>，具体以您通过合法渠道获得的<strong>激活码</strong>在服务端验证通过后所开通的会员/授权服务为准。该对价<strong>不构成</strong>对任何第三方交易平台、文交所或数字资产本身的购买、托管或收益承诺。
            </p>
          </section>

          <section class="eula-block">
            <h2>二、关于激活体验（产品说明）</h2>
            <p>
              本产品设计注重让您在完成<strong>激活码验证</strong>时获得清晰、顺畅的<strong>授权开通体验</strong>，该体验属于产品交互与授权流程的一部分。<strong>您为激活码/会员服务所支付的价格，不视为对软件内任一具体功能模块的单独计价、分级或效果保证</strong>；功能列表、界面展示不构成对价拆分依据。
            </p>
          </section>

          <section class="eula-block">
            <h2>三、功能与价格的关系</h2>
            <p>
              软件内提供的<strong>工具性、辅助性功能</strong>（例如但不限于：信息展示、统计汇总、提醒、界面与操作便利相关能力等）旨在提升使用体验，其<strong>是否提供、如何展示、是否随版本调整或下线，均由我们合理裁量</strong>，且<strong>与激活码价格无逐项对应关系</strong>。<strong>付费不默示</strong>任一功能必然可用、达到特定结果或满足特定投资目标。
            </p>
          </section>

          <section class="eula-block">
            <h2>四、非投资建议与第三方信息</h2>
            <p>
              本软件可能协助您访问或整理来自<strong>第三方</strong>（如交易平台、文交所、公开接口等）的信息。我们<strong>并非</strong>该等主体的运营方或代理人，<strong>不对</strong>第三方数据的准确性、完整性、及时性、合法性作担保。软件内任何数据、榜单、统计、提示均<strong>仅供您个人参考</strong>，<strong>不构成</strong>证券、期货、数字藏品或其他任何类别的<strong>投资建议、推介或收益承诺</strong>。
            </p>
          </section>

          <section class="eula-block">
            <h2>五、名人堂及类似展示</h2>
            <p>
              若软件内存在基于用户自愿公开数据形成的排行或展示（如「名人堂」等），该等内容<strong>仅为客观统计展示</strong>，<strong>不构成</strong>对任何主体的信用、资质或道德评价，<strong>亦不构成</strong>投资建议。投资有风险，您应独立决策并自担后果。
            </p>
          </section>

          <section class="eula-block">
            <h2>六、责任限制</h2>
            <p>
              在适用法律允许的最大范围内，因您使用或无法使用本软件、因信赖软件内任何信息而作出的交易或投资决定所产生的直接或间接损失，<strong>本软件提供方不承担赔偿责任</strong>。本软件按「现状」及「现有」基础提供，<strong>不保证</strong>无中断、无错误、无漏洞或适用于您的特定用途。
            </p>
          </section>

          <section class="eula-block">
            <h2>七、接受与变更</h2>
            <p>
              您勾选「我已阅读并同意」并点击「同意并继续」，即表示您已<strong>充分阅读、理解并自愿接受</strong>本协议全部内容。<strong>若您不同意，请勿使用本软件。</strong>我们可能适时更新本说明；重大变更时可能通过软件内提示等方式告知；您继续使用视为接受更新后的规则（法律另有规定的除外）。
            </p>
          </section>

          <p class="eula-footnote">
            提示：若您为限制民事行为能力人或无民事行为能力人，请在监护人陪同下阅读并在监护人同意后使用。
          </p>
        </div>

        <div class="eula-check-row">
          <van-checkbox v-model="checked" icon-size="18" shape="square">
            <span class="eula-check-label">我已完整阅读并同意上述用户协议与免责声明</span>
          </van-checkbox>
        </div>

        <div class="eula-actions">
          <van-button
            block
            round
            type="primary"
            class="eula-btn-primary"
            :disabled="!checked"
            @click="onAgree"
          >
            同意并继续
          </van-button>
          <van-button block round type="default" class="eula-btn-secondary" native-type="button" @click="onDisagree">
            不同意并退出
          </van-button>
        </div>
      </div>
    </div>
  </van-overlay>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import { Capacitor } from '@capacitor/core'
import { isUserAgreementAccepted, setUserAgreementAccepted } from '@/utils/userAgreement'
import { shouldSkipUserAgreement } from '@/utils/publicRoutes'

const route = useRoute()
const visible = ref(false)
const checked = ref(false)

function syncVisible() {
  if (shouldSkipUserAgreement(route.path)) {
    visible.value = false
    return
  }
  visible.value = !isUserAgreementAccepted()
}

watch(() => route.path, syncVisible, { immediate: true })

function onAgree() {
  if (!checked.value) {
    showToast('请先勾选同意')
    return
  }
  setUserAgreementAccepted()
  visible.value = false
}

async function onDisagree() {
  if (Capacitor.isNativePlatform()) {
    try {
      const { App } = await import('@capacitor/app')
      await App.exitApp()
    } catch (e) {
      console.error(e)
      showToast('无法退出，请从多任务中划掉应用')
    }
    return
  }
  showToast('请关闭本页面或浏览器标签页')
}
</script>

<style scoped>
.eula-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.72) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 16px;
  box-sizing: border-box;
  height: 100%;
}

.eula-shell {
  width: 100%;
  max-width: 440px;
  height: min(92vh, 720px);
  max-height: min(92vh, 720px);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.eula-card {
  height: 100%;
  min-height: 0;
  background: var(--app-surface-glass, #fff);
  border-radius: 20px;
  border: 1px solid var(--app-border-subtle, rgba(0, 0, 0, 0.08));
  box-shadow: var(--app-card-shadow, 0 16px 48px rgba(0, 0, 0, 0.14));
  padding: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.eula-header {
  flex-shrink: 0;
  padding: 20px 20px 14px;
  text-align: center;
  background: linear-gradient(180deg, rgba(25, 137, 250, 0.06) 0%, transparent 100%);
  border-bottom: 1px solid var(--app-border-subtle, rgba(0, 0, 0, 0.06));
}

.eula-title {
  margin: 0 0 10px;
  font-size: 19px;
  font-weight: 700;
  color: var(--app-text-primary, #1a1a1a);
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.eula-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--app-text-muted, #646566);
}

.eula-scroll {
  flex: 1 1 0;
  min-height: max(200px, min(38vh, 280px));
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  margin: 0 16px;
  padding: 14px 14px 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 137, 250, 0.45) rgba(0, 0, 0, 0.04);
}

.eula-scroll::-webkit-scrollbar {
  width: 5px;
}

.eula-scroll::-webkit-scrollbar-thumb {
  background: rgba(25, 137, 250, 0.35);
  border-radius: 3px;
}

.eula-block {
  margin-bottom: 16px;
}

.eula-block:last-of-type {
  margin-bottom: 12px;
}

.eula-block h2 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--app-text-primary, #323233);
  line-height: 1.35;
}

.eula-block p {
  margin: 0;
  font-size: 13px;
  line-height: 1.72;
  color: var(--app-text-secondary, #646566);
  text-align: justify;
  word-break: break-word;
}

.eula-footnote {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--app-text-muted, #969799);
}

.eula-check-row {
  flex-shrink: 0;
  padding: 14px 20px 8px;
}

.eula-check-row :deep(.van-checkbox__label) {
  margin-left: 8px;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 600;
  color: var(--app-text-primary, #323233);
}

.eula-check-label {
  font-size: 13px;
  line-height: 1.5;
  font-weight: 600;
  color: var(--app-text-primary, #323233);
}

.eula-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 6px 20px 20px;
}

.eula-btn-primary {
  height: 48px !important;
  font-size: 16px !important;
  font-weight: 650;
  background: linear-gradient(135deg, var(--app-buy-gradient-from, var(--van-primary-color)), var(--app-buy-gradient-to, #e85d1a)) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(25, 137, 250, 0.28);
}

.eula-btn-primary:disabled {
  opacity: 0.45;
  box-shadow: none;
}

.eula-btn-secondary {
  height: 48px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #323233 !important;
  background: #f7f8fa !important;
  border: 1.5px solid rgba(50, 50, 51, 0.22) !important;
}

.eula-btn-secondary:active {
  opacity: 0.92;
}
</style>
